/* What the play button plays.
 *
 * Extracted from Workspace's JSX because it is the one part of the testing
 * round with real logic in it, and inline in a prop it could only be verified
 * by clicking. The rest of that round is DOM wiring — focus, a seek call,
 * markup — which needs a DOM to test and there is no jsdom in this project.
 */

import type { Subtask, Task } from "../api/types";

export interface LoopSpan {
  start: number;
  end: number;
}

/** The span playback wraps inside, for the TASK / SUB-TASK toggle (3.3.4).
 *
 * BOTH MODES LOOP. TASK used to return null, which the player reads as "do not
 * wrap" — so it ran to the end of the VIDEO, through every task after this one.
 * Testing asked for "when Task option is selected then loop through that
 * particular task and not all the Tasks. To move to another task, user will
 * click manually", which is the shape the control implies: two scopes, neither
 * of them "everything".
 *
 * A task's span is its sub-tasks' outer edges rather than any stored bound,
 * because the sub-tasks tile the task exactly — `subtask[i].end ==
 * subtask[i+1].start` — and splits and merges move those edges live. Reading
 * them keeps the loop correct across an edit without a reload.
 *
 * Returns null only when there is nothing to loop, which the player treats as
 * "play straight on".
 */
export function loopSpanFor(
  mode: "task" | "subtask",
  task: Task | null,
  sub: Subtask | null,
): LoopSpan | null {
  if (mode === "subtask") {
    if (!sub) return null;
    return { start: sub.start_seconds, end: sub.end_seconds };
  }
  const subs = task?.subtasks;
  if (!subs?.length) return null;
  // Not subs[0] and subs[at-1] blindly: the array is ordered, but a span with
  // end <= start would make the player wrap on every frame and freeze the
  // picture on one still. Take the real extent.
  const start = Math.min(...subs.map((s) => s.start_seconds));
  const end = Math.max(...subs.map((s) => s.end_seconds));
  return end > start ? { start, end } : null;
}


/** Where PLAY should begin, given where the playhead is and what is selected.
 *
 *  Returns a time to seek to first, or null to just resume.
 *
 *  THE RULE, AND THE TWO CASES IT HAS TO TELL APART.
 *
 *  Testing, 16 Sep: "no matter which subtask is selected, clicking play always
 *  starts playing from the start of the video." Nothing tied PLAY to the
 *  selection, so in TASK loop mode — whose span is the whole task, and for a
 *  video of tiled tasks that begins at 0 — a playhead at or past the end
 *  wrapped to the beginning the moment playback started.
 *
 *  So play seeks to the selected sub-task. But NOT always: pausing in the
 *  middle of a sub-task and pressing play again must continue from where it
 *  stopped, because that is what pause means. Seeking on every play would make
 *  the pause button unable to hold a position.
 *
 *  Outside the span -> start of it. Inside -> resume. `end` is exclusive, so a
 *  playhead resting exactly on the boundary belongs to the next sub-task and
 *  counts as outside this one.
 */
export function playStartFor(
  playhead: number,
  selected: LoopSpan | null,
): number | null {
  if (!selected) return null;
  const outside = playhead < selected.start || playhead >= selected.end;
  return outside ? selected.start : null;
}

/** How far into a sub-task you have to be before « restarts it instead of
 *  going back one. The convention every media player uses: the first press
 *  takes you to the top of what is playing, a second press goes back. Without
 *  it, « one second into a sub-task jumps to the previous one and you lose the
 *  thing you were about to re-watch. */
const RESTART_WINDOW_SECONDS = 0.75;

/** Where « and » should land, given the sub-task boundaries of the video.
 *
 *  THE BUG THIS REPLACES — testing, 21 Sep: "Forward and Backward arrows of the
 *  video player are skipping one subtask. If forward is taken on the first
 *  sub-task, the third sub-task gets played."
 *
 *  They were `seek(now ± 5)`. Five seconds is an arbitrary distance in a tool
 *  where the unit of work is a sub-task, and real sub-tasks run 1.2s to 5.4s —
 *  so a 5s jump crosses one, two or none depending entirely on where you
 *  happened to be. The buttons did what their tooltips said and still behaved
 *  unpredictably, because the number had no relationship to the content.
 *
 *  Stepping by boundary makes them exact: » is always the next sub-task, «
 *  always the start of this one or the previous. Frame-stepping (‹𝗙 𝗙›) remains
 *  the fine control, so nothing is lost by making these coarse and predictable.
 *
 *  `boundaries` is every sub-task start time, ascending. Returns null when
 *  there is nowhere to go — the caller should disable the button rather than
 *  seek to a no-op.
 */
export function stepToBoundary(
  now: number,
  boundaries: readonly number[],
  direction: 1 | -1,
): number | null {
  if (!boundaries.length) return null;
  // Tolerance, because `now` comes from the video element and lands a hair
  // either side of the boundary it was seeked to. Without it, » sitting exactly
  // on a start would return that same start and the button would do nothing.
  const EPS = 0.001;

  if (direction === 1) {
    const next = boundaries.find((b) => b > now + EPS);
    return next ?? null;
  }

  // Backwards: the start of the sub-task containing `now`...
  const currentStart = [...boundaries].reverse().find((b) => b <= now + EPS);
  if (currentStart === undefined) return null;
  // ...unless we have only just entered it, in which case go back one.
  if (now - currentStart > RESTART_WINDOW_SECONDS) return currentStart;
  const prev = [...boundaries].reverse().find((b) => b < currentStart - EPS);
  return prev ?? (now > currentStart + EPS ? currentStart : null);
}
